import { doc, getDoc, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { auth } from "@/lib/firebase";
import {
  isPremiumActive,
  MenuCardSchema,
  type MenuCard,
  type Plan,
  type Wedding,
  type WeddingSettings,
  type WebsiteConfig,
} from "@amazing-wedding/shared-types";

/** Thrown when the active user's plan does not allow the requested write. */
export class PlanRequiredError extends Error {
  constructor(message = "Premium wymagany") {
    super(message);
    this.name = "PlanRequiredError";
  }
}

/**
 * Wedding eligibility for publishing follows the OWNER's plan (the owner
 * is the one billed). The canonical gate lives in
 * `apps/functions/src/api/public-site.ts` which loads the owner's user
 * doc with the Admin SDK; we mirror as much as Firestore rules allow on
 * the client:
 *
 *  - When the signed-in user IS the owner, we read their own user doc and
 *    block the write if `plan !== "premium"`. This gives an instant
 *    friendly error instead of a silent "published but dark".
 *  - When the signed-in user is the partner, Firestore rules forbid
 *    reading the owner's user doc, so we trust the server gate — the
 *    write proceeds and the canonical 402 response from `public-site.ts`
 *    decides whether the site goes live for guests.
 */
async function assertPremiumForPublish(weddingId: string): Promise<void> {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new PlanRequiredError("Musisz być zalogowany/a, aby opublikować stronę.");
  const weddingSnap = await getDoc(doc(db, "weddings", weddingId));
  const ownerId = (weddingSnap.data()?.ownerId as string | undefined) ?? null;
  if (!ownerId || ownerId !== uid) {
    // Partner publish or unknown owner — defer to server gate.
    return;
  }
  const snap = await getDoc(doc(db, "users", uid));
  const data = snap.data();
  const plan: Plan = (data?.plan as Plan | undefined) ?? "free";
  if (
    !isPremiumActive({
      plan,
      planValidUntil: data?.planValidUntil as string | null | undefined,
      mollieSubscriptionId: data?.mollieSubscriptionId as string | null | undefined,
    })
  ) {
    throw new PlanRequiredError(
      "Publikacja strony jest dostępna w planie Premium. Przejdź na Premium, aby udostępnić stronę gościom.",
    );
  }
}

/**
 * Patch-shaped editor for wedding-level fields. Only exposes the fields
 * that the Settings screen edits today — couple names, date, venue,
 * estimated guest count, handle, and the embedded WeddingSettings
 * (budget, currency, locale, country, timezone).
 *
 * Doc-level write so it touches the wedding document directly. Firestore
 * rules already restrict this to the owner / partner.
 */
export type WeddingPatch = Partial<
  Pick<
    Wedding,
    | "coupleName1"
    | "coupleName2"
    | "weddingDate"
    | "venue"
    | "venueBooked"
    | "ceremonyType"
    | "estimatedGuestCount"
    | "handle"
    | "title"
    | "seatingCanvasInset"
    | "seatingRoomWidthM"
    | "dayPlanFeedToken"
  >
> & {
  settings?: Partial<WeddingSettings>;
};

export async function updateWedding(weddingId: string, patch: WeddingPatch): Promise<void> {
  const ref = doc(db, "weddings", weddingId);
  // Build a flattened patch: nested settings fields use dot-paths so
  // Firestore merges instead of clobbering the whole settings object.
  const payload: Record<string, unknown> = {
    updatedAt: serverTimestamp(),
  };
  for (const [k, v] of Object.entries(patch)) {
    if (k === "settings" && v && typeof v === "object") {
      for (const [sk, sv] of Object.entries(v)) {
        payload[`settings.${sk}`] = sv;
      }
    } else {
      payload[k] = v;
    }
  }
  await updateDoc(ref, payload);
}

export async function updateMenuCard(weddingId: string, menuCard: MenuCard): Promise<void> {
  const parsed = MenuCardSchema.parse({
    ...menuCard,
    updatedAt: new Date().toISOString(),
  });
  const ref = doc(db, "weddings", weddingId);
  await updateDoc(ref, {
    menuCard: parsed,
    updatedAt: serverTimestamp(),
  });
}

/** Persist the full website config block. Uses dot-notation for content map so nested values merge. */
export async function updateWebsiteConfig(
  weddingId: string,
  patch: Partial<WebsiteConfig>,
): Promise<void> {
  // Defense-in-depth: refuse to flip the site live for free users on the
  // client side. The canonical gate is in apps/functions/src/api/public-site.ts
  // (which inspects the wedding owner's plan), but blocking here gives an
  // immediate, friendly error rather than a silent "published but invisible".
  if (patch.published === true) {
    await assertPremiumForPublish(weddingId);
  }
  const ref = doc(db, "weddings", weddingId);
  const payload: Record<string, unknown> = { updatedAt: serverTimestamp() };

  for (const [k, v] of Object.entries(patch)) {
    if (k === "content" && v && typeof v === "object") {
      for (const [sectionId, text] of Object.entries(v)) {
        payload[`website.content.${sectionId}`] = text;
      }
    } else if (k === "localized" && v && typeof v === "object") {
      for (const [locale, bundle] of Object.entries(v as Record<string, unknown>)) {
        if (!bundle || typeof bundle !== "object") continue;
        for (const [field, fieldVal] of Object.entries(bundle as Record<string, unknown>)) {
          if (field === "content" && fieldVal && typeof fieldVal === "object") {
            for (const [sectionId, text] of Object.entries(fieldVal as Record<string, unknown>)) {
              payload[`website.localized.${locale}.content.${sectionId}`] = text;
            }
          } else {
            payload[`website.localized.${locale}.${field}`] = fieldVal;
          }
        }
      }
    } else if (k === "sectionBackgrounds" && v && typeof v === "object") {
      for (const [sectionId, color] of Object.entries(v)) {
        payload[`website.sectionBackgrounds.${sectionId}`] = color;
      }
    } else {
      payload[`website.${k}`] = v;
    }
  }

  await updateDoc(ref, payload);
}
